import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Anchor, ArrowLeft, CheckCircle2, Globe, Megaphone, Palette, Sparkles, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const SERVICE_LABELS = {
  website: { label: 'Website Creation', icon: <Globe className="w-5 h-5"/>, desc: "We'll discuss your goals, audience, and the kind of site you need." },
  marketing: { label: 'Marketing Solutions', icon: <Megaphone className="w-5 h-5"/>, desc: "We'll audit your current marketing and map out the campaigns to launch." },
  branding: { label: 'Brand Identity', icon: <Palette className="w-5 h-5"/>, desc: "We'll explore your story, values, and visual direction." },
  general: { label: 'General Enquiry', icon: <Sparkles className="w-5 h-5"/>, desc: "Not sure where to start? Let's chat and figure it out together." },
};

export default function Consultation() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const initialService = params.get('service') || 'general';
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    business_name: '',
    service: initialService,
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => { setForm(f => ({ ...f, service: initialService })); }, [initialService]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/consultation/request`, form);
      setSuccess(true);
      toast.success('Booking received!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen pt-10 pb-20">
      <nav className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[var(--ocean-surface)] to-[var(--ocean-deep)] flex items-center justify-center"><Anchor className="w-5 h-5 text-white" /></div>
          <span className="font-bold text-lg text-[var(--ocean-deep)]" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>SAIL Services SA</span>
        </Link>
        <Link to="/" className="text-sm text-[var(--text-secondary)] hover:text-[var(--ocean-deep)] flex items-center gap-1"><ArrowLeft className="w-4 h-4"/> Back to home</Link>
      </nav>
      <div className="max-w-6xl mx-auto px-6 mt-8 grid lg:grid-cols-5 gap-10">
        <aside className="lg:col-span-2">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--ocean-coral)]/10 border border-[var(--ocean-coral)]/30 text-xs font-bold text-[var(--ocean-coral)] mb-4">
            <Sparkles className="w-3.5 h-3.5"/> First consult is free
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-[var(--ocean-deep)] leading-tight" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>
            Book your free consultation.
          </h1>
          <p className="mt-4 text-[var(--text-secondary)]">
            A no-strings, one-hour conversation. We'll listen to where your brand is and map out where it could go.
          </p>
          <div className="mt-7 space-y-3">
            {Object.entries(SERVICE_LABELS).map(([k, v]) => (
              <button
                key={k}
                type="button"
                onClick={() => setForm({ ...form, service: k })}
                className={`w-full text-left p-4 rounded-2xl border transition-all ${form.service === k ? 'bg-gradient-to-br from-[var(--ocean-surface)] to-[var(--ocean-deep)] text-white border-transparent shadow-lg' : 'bg-white/70 backdrop-blur-md border-[var(--border-glass)] hover:border-[var(--ocean-surface)]'}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${form.service === k ? 'bg-white/15' : 'bg-[var(--ocean-surface)]/10 text-[var(--ocean-surface)]'}`}>{v.icon}</div>
                  <div>
                    <p className={`font-bold ${form.service === k ? 'text-white' : 'text-[var(--ocean-deep)]'}`}>{v.label}</p>
                    <p className={`text-sm ${form.service === k ? 'text-white/80' : 'text-[var(--text-secondary)]'}`}>{v.desc}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
          <div className="mt-6 p-5 rounded-2xl bg-white/70 backdrop-blur-md border border-[var(--border-glass)] flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-[var(--ocean-surface)] flex-shrink-0" />
            <p className="text-sm text-[var(--text-secondary)]">No commitment. No spam. We'll only contact you about your consultation.</p>
          </div>
        </aside>
        <main className="lg:col-span-3">
          {success ? (
            <div className="rounded-3xl bg-gradient-to-br from-[var(--ocean-surface)] to-[var(--ocean-deep)] text-white p-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-white/15 flex items-center justify-center mx-auto mb-5"><CheckCircle2 className="w-8 h-8"/></div>
              <h2 className="text-3xl font-extrabold" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>Thanks, {form.name.split(' ')[0]}!</h2>
              <p className="mt-3 text-white/85">We've received your request and will email you within one working day to set up a time.</p>
              <Link to="/" className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white text-[var(--ocean-deep)] font-semibold">Back to home</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-3xl bg-white/80 backdrop-blur-xl border border-[var(--border-glass)] p-7 md:p-10 shadow-xl shadow-[var(--ocean-surface)]/10 space-y-4">
              <h2 className="text-2xl font-bold text-[var(--ocean-deep)]" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>Tell us about you</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Full name" required value={form.name} onChange={v=>setForm({...form, name:v})} />
                <Field label="Email" type="email" required value={form.email} onChange={v=>setForm({...form, email:v})} />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Phone (optional)" value={form.phone} onChange={v=>setForm({...form, phone:v})} />
                <Field label="Business name (optional)" value={form.business_name} onChange={v=>setForm({...form, business_name:v})} />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[var(--text-muted)] mb-1.5">Tell us about your project</label>
                <textarea
                  rows={5}
                  className="w-full px-4 py-3 rounded-xl border border-[var(--border-glass)] bg-white focus:outline-none focus:border-[var(--ocean-surface)] resize-none"
                  value={form.message}
                  onChange={e => setForm({ ...form, message: e.target.value })}
                  placeholder="What are you trying to achieve?"
                />
              </div>
              <button type="submit" disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-gradient-to-br from-[var(--ocean-surface)] to-[var(--ocean-deep)] text-white font-semibold shadow-lg shadow-[var(--ocean-surface)]/30 hover:-translate-y-0.5 transition-all disabled:opacity-50">
                {submitting ? 'Sending…' : 'Book my free consultation'}
              </button>
            </form>
          )}
        </main>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', required }) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-wider text-[var(--text-muted)] mb-1.5">{label}{required && ' *'}</label>
      <input required={required} type={type} value={value} onChange={e=>onChange(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-[var(--border-glass)] bg-white focus:outline-none focus:border-[var(--ocean-surface)]" />
    </div>
  );
}
